/**
 * Inline photo field for the booking belongings form — sub-spec 07 Plan 3 M10.
 *
 * Drives the per-booking presign/confirm endpoints + bubbles the resulting
 * `fileId` up to the parent's add-belonging form via `onPhotoChange`.
 *
 * The component is decoupled from the actual belongings POST — the parent
 * decides when to include `photoFileId` in the eventual create call. This
 * is the "upload first, then attach" pattern from spec §17.
 */
import { useState } from 'react';
import { FileUploader } from './FileUploader';

export interface BelongingPhotoFieldProps {
  bookingId: string;
  /** Called when a photo finishes uploading (or when cleared via null). */
  onPhotoChange: (fileId: string | null) => void;
}

export function BelongingPhotoField({ bookingId, onPhotoChange }: BelongingPhotoFieldProps) {
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);

  function handleUploaded(fileId: string) {
    setUploadedFileId(fileId);
    onPhotoChange(fileId);
  }

  function handleClear() {
    setUploadedFileId(null);
    onPhotoChange(null);
  }

  if (uploadedFileId) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-success">Photo attached</span>
        <button
          type="button"
          onClick={handleClear}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <FileUploader
      presignUrl={`/api/bookings/${bookingId}/belongings/photo/presign`}
      confirmUrl={`/api/bookings/${bookingId}/belongings/photo/confirm`}
      accept="image/jpeg,image/png,image/webp,image/heic"
      label="Attach photo"
      onUploaded={handleUploaded}
    />
  );
}
